/* eslint-disable react-refresh/only-export-components */
import { Copy, Plus, RotateCcw, Save, Trash2, Webhook } from "lucide-react";
import type { RaRecord } from "ra-core";
import {
  EditBase,
  Form,
  useCanAccess,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toSlug } from "@/lib/toSlug";
import { formatPriceInINR } from "@/lib/formatters";
import type { AgentCommissionRule } from "@/types/realEstate";
import {
  DEFAULT_WHATSAPP_CAMPAIGN_TEMPLATES,
  DEFAULT_WHATSAPP_CHAT_PRESETS,
  MAX_WHATSAPP_CHAT_PRESETS,
  normalizeWhatsAppCampaignTemplates,
  normalizeWhatsAppChatPresets,
} from "@/types/realEstate";
import {
  computeCommissions,
  DEFAULT_AGENT_COMMISSION_RULE,
  DEFAULT_LATE_INTEREST_ANNUAL_PERCENT,
  normalizeLateInterestAnnualPercent,
} from "../bookings/bookingUtils";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import ImageEditorField from "../misc/ImageEditorField";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";

const SECTIONS = [
  {
    id: "branding",
    label: "crm.settings.sections.branding",
    fallback: "Branding",
  },
  {
    id: "lead-integrations",
    label: "crm.settings.sections.lead_integrations",
    fallback: "Lead integrations",
  },
  {
    id: "companies",
    label: "resources.companies.name",
    fallback: "Companies",
  },
  { id: "deals", label: "resources.deals.name", fallback: "Deals" },
  { id: "notes", label: "resources.notes.name", fallback: "Notes" },
  { id: "tasks", label: "resources.tasks.name", fallback: "Tasks" },
  {
    id: "agent-commission",
    label: "crm.settings.sections.agent_commission",
    fallback: "Agent commission",
  },
  {
    id: "late-interest",
    label: "crm.settings.sections.late_interest",
    fallback: "Late interest",
  },
  {
    id: "hold-exclusivity",
    label: "crm.settings.sections.hold_exclusivity",
    fallback: "Hold exclusivity",
  },
  {
    id: "portal-spend",
    label: "crm.settings.sections.portal_spend",
    fallback: "Portal spend",
  },
  {
    id: "whatsapp-templates",
    label: "crm.settings.sections.whatsapp_templates",
    fallback: "WhatsApp templates",
  },
  {
    id: "whatsapp-chat-presets",
    label: "crm.settings.sections.whatsapp_chat_presets",
    fallback: "WhatsApp chat presets",
  },
];

/** Portals wired to portal-ingest / email-parser (webhook URLs). */
export const LEAD_PORTAL_SOURCES = [
  {
    id: "99acres",
    label: "99acres",
    portalParam: "99acres",
    delivery: "Partner webhook or email → portal-email-parser",
  },
  {
    id: "MagicBricks",
    label: "MagicBricks",
    portalParam: "MagicBricks",
    delivery: "Partner webhook or email → portal-email-parser",
  },
  {
    id: "Housing.com",
    label: "Housing.com",
    portalParam: "Housing.com",
    delivery: "Partner webhook or email → portal-email-parser",
  },
  {
    id: "NoBroker",
    label: "NoBroker",
    portalParam: "NoBroker",
    delivery: "Partner webhook or email → portal-email-parser",
  },
  {
    id: "Meta_Ads",
    label: "Meta Ads",
    portalParam: "Meta_Ads",
    delivery: "Meta Lead Ads webhook",
  },
  {
    id: "Google_Ads",
    label: "Google Ads",
    portalParam: "Google_Ads",
    delivery: "Google Lead Form webhook / Zapier",
  },
] as const;

/** lead_source enum keys for portal spend / CPL (persisted object keys). */
export const PORTAL_SPEND_SOURCES = [
  { id: "99acres", label: "99acres" },
  { id: "MagicBricks", label: "MagicBricks" },
  { id: "Housing.com", label: "Housing.com" },
  { id: "Meta_Ads", label: "Meta Ads" },
  { id: "Google_Ads", label: "Google Ads" },
  { id: "Walk_In", label: "Walk-In" },
  { id: "Channel_Partner", label: "Channel Partner" },
  { id: "Website", label: "Website" },
  { id: "NoBroker", label: "NoBroker" },
] as const;

/** Legacy display-label keys → lead_source enum (portal spend migration). */
const LEGACY_PORTAL_SPEND_KEYS: Record<string, string> = {
  "Meta Ads": "Meta_Ads",
  "Google Ads": "Google_Ads",
};

export const migratePortalSpendKeys = (
  raw: Record<string, number>,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    const enumKey = LEGACY_PORTAL_SPEND_KEYS[key] || key;
    out[enumKey] = (out[enumKey] || 0) + value;
  }
  return out;
};

export function buildPortalIngestUrl(
  supabaseUrl: string,
  portalParam: string,
): string {
  const base = (supabaseUrl || "").replace(/\/$/, "");
  if (!base) return "";
  return `${base}/functions/v1/portal-ingest?portal=${encodeURIComponent(portalParam)}`;
}

export function summarizeLeadSourcesByPortal(
  contacts: Array<{
    lead_source?: string | null;
    last_seen?: string | null;
    first_seen?: string | null;
    created_at?: string | null;
  }>,
): Record<string, { count: number; lastAt: string | null }> {
  const out: Record<string, { count: number; lastAt: string | null }> = {};
  for (const c of contacts) {
    const source = String(c.lead_source || "").trim();
    if (!source) continue;
    const at = c.last_seen || c.first_seen || c.created_at || null;
    const cur = out[source] || { count: 0, lastAt: null as string | null };
    cur.count += 1;
    if (
      at &&
      (!cur.lastAt || new Date(at).getTime() > new Date(cur.lastAt).getTime())
    ) {
      cur.lastAt = at;
    }
    out[source] = cur;
  }
  return out;
}

/** Aggregate inbound_leads rows from the last 24 hours by portal. */
export function summarizeInboundLeadsLast24h(
  rows: Array<{
    portal?: string | null;
    parse_ok?: boolean | null;
    created_at?: string | null;
  }>,
  nowMs = Date.now(),
): { byPortal: Record<string, number>; ok: number; failed: number } {
  const cutoff = nowMs - 24 * 60 * 60 * 1000;
  const byPortal: Record<string, number> = {};
  let ok = 0;
  let failed = 0;
  for (const row of rows) {
    const t = row.created_at ? new Date(row.created_at).getTime() : 0;
    if (!t || t < cutoff) continue;
    const portal = String(row.portal || "Unknown");
    byPortal[portal] = (byPortal[portal] || 0) + 1;
    if (row.parse_ok) ok += 1;
    else failed += 1;
  }
  return { byPortal, ok, failed };
}

function formatLastLeadAt(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  return new Date(iso).toLocaleString();
}

/** Ensure every item in a { value, label } array has a value (slug from label). */
const ensureValues = (items: { value?: string; label: string }[] | undefined) =>
  items?.map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Check for duplicate slugs
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

const SAMPLE_PREVIEW_AGREEMENT = 10_000_000;

export const validateAgentCommissionRule = (
  rule: AgentCommissionRule | undefined | null,
): string | undefined => {
  if (!rule) {
    return "Agent commission rule is required";
  }
  if (rule.type === "percent") {
    const rate = Number(rule.rate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      return "Commission rate must be between 0 and 100";
    }
    return undefined;
  }
  if (rule.type === "slab") {
    if (!rule.slabs?.length) {
      return "At least one commission slab is required";
    }
    for (const slab of rule.slabs) {
      const min = Number(slab.minAgreementValue);
      const rate = Number(slab.rate);
      if (!Number.isFinite(min) || min < 0) {
        return "Slab thresholds must be non-negative";
      }
      if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
        return "Slab rates must be between 0 and 100";
      }
    }
    return undefined;
  }
  return "Invalid agent commission rule";
};

export const validatePortalSpend = (
  raw: Record<string, unknown> | undefined | null,
): string | undefined => {
  if (!raw || typeof raw !== "object") return undefined;
  for (const [key, val] of Object.entries(raw)) {
    if (val === "" || val === undefined || val === null) continue;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      return `Portal spend for ${key} must be a non-negative number`;
    }
  }
  return undefined;
};

export const normalizePortalSpend = (
  raw: Record<string, unknown> | undefined | null,
): Record<string, number> => {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [key, val] of Object.entries(raw)) {
    if (val === "" || val === undefined || val === null) continue;
    const n = Number(val);
    if (Number.isFinite(n) && n >= 0) {
      out[key] = n;
    }
  }
  return migratePortalSpendKeys(out);
};

const toPortalSpendFormValues = (
  stored: Record<string, number> | undefined,
): Record<string, number | ""> => {
  const migrated = stored ? migratePortalSpendKeys(stored) : {};
  return PORTAL_SPEND_SOURCES.reduce(
    (acc, portal) => {
      const value = migrated[portal.id];
      acc[portal.id] = value != null && Number.isFinite(value) ? value : "";
      return acc;
    },
    {} as Record<string, number | "">,
  );
};

const normalizeAgentCommissionRule = (
  rule: AgentCommissionRule | undefined | null,
): AgentCommissionRule => {
  if (!rule || rule.type === "percent") {
    const rate = Number(
      rule?.type === "percent" ? rule.rate : DEFAULT_AGENT_COMMISSION_RULE.rate,
    );
    return {
      type: "percent",
      rate: Number.isFinite(rate) ? rate : DEFAULT_AGENT_COMMISSION_RULE.rate,
    };
  }
  return {
    type: "slab",
    slabs: (rule.slabs ?? []).map((slab) => ({
      minAgreementValue: Number(slab.minAgreementValue) || 0,
      rate: Number(slab.rate) || 0,
    })),
  };
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["en"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

const transformFormValues = (data: Record<string, any>) => ({
  config: {
    title: data.title,
    lightModeLogo: data.lightModeLogo,
    darkModeLogo: data.darkModeLogo,
    currency: data.currency,
    companySectors: ensureValues(data.companySectors),
    dealCategories: ensureValues(data.dealCategories),
    taskTypes: ensureValues(data.taskTypes),
    dealStages: ensureValues(data.dealStages),
    dealPipelineStatuses: data.dealPipelineStatuses,
    noteStatuses: ensureValues(data.noteStatuses),
    agentCommission: normalizeAgentCommissionRule(data.agentCommission),
    portalSpend: normalizePortalSpend(data.portalSpend),
    whatsappCampaignTemplates: normalizeWhatsAppCampaignTemplates(
      data.whatsappCampaignTemplates,
    ),
    whatsappChatPresets: normalizeWhatsAppChatPresets(data.whatsappChatPresets),
    lateInterestAnnualPercent: normalizeLateInterestAnnualPercent(
      data.lateInterestAnnualPercent,
    ),
    restrictHoldChangesToHolder: data.restrictHoldChangesToHolder !== false,
  } as ConfigurationContextValue & {
    agentCommission: AgentCommissionRule;
    portalSpend: Record<string, number>;
    whatsappCampaignTemplates: ReturnType<
      typeof normalizeWhatsAppCampaignTemplates
    >;
    whatsappChatPresets: ReturnType<typeof normalizeWhatsAppChatPresets>;
  },
});

export const SettingsPage = () => {
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const config = useConfigurationContext();
  const configWithCommission = config as ConfigurationContextValue & {
    agentCommission?: AgentCommissionRule;
    portalSpend?: Record<string, number>;
    whatsappCampaignTemplates?: unknown;
    whatsappChatPresets?: unknown;
  };

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      currency: config.currency,
      companySectors: config.companySectors,
      dealCategories: config.dealCategories,
      taskTypes: config.taskTypes,
      dealStages: config.dealStages,
      dealPipelineStatuses: config.dealPipelineStatuses,
      noteStatuses: config.noteStatuses,
      agentCommission: normalizeAgentCommissionRule(
        configWithCommission.agentCommission,
      ),
      portalSpend: toPortalSpendFormValues(configWithCommission.portalSpend),
      whatsappCampaignTemplates: normalizeWhatsAppCampaignTemplates(
        configWithCommission.whatsappCampaignTemplates,
      ),
      whatsappChatPresets: normalizeWhatsAppChatPresets(
        configWithCommission.whatsappChatPresets,
      ),
      lateInterestAnnualPercent: normalizeLateInterestAnnualPercent(
        config.lateInterestAnnualPercent,
      ),
      restrictHoldChangesToHolder: config.restrictHoldChangesToHolder !== false,
    }),
    [
      config,
      configWithCommission.agentCommission,
      configWithCommission.portalSpend,
      configWithCommission.whatsappCampaignTemplates,
      configWithCommission.whatsappChatPresets,
    ],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  const { canAccess: canEditCommission } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  const {
    watch,
    setValue,
    reset,
    formState: { isSubmitting },
  } = useFormContext();

  const dealStages = watch("dealStages");
  const dealPipelineStatuses: string[] = watch("dealPipelineStatuses") ?? [];
  const stageDisplayName = translate("crm.settings.validation.entities.stages");
  const categoryDisplayName = translate(
    "crm.settings.validation.entities.categories",
  );

  const { data: deals } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const validateDealStages = useCallback(
    (stages: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(stages, deals, "stage", stageDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
        inUse: (displayName, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: displayName,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [deals, stageDisplayName, translate],
  );

  const validateDealCategories = useCallback(
    (categories: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(categories, deals, "category", categoryDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
        inUse: (displayName, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: displayName,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [categoryDisplayName, deals, translate],
  );

  return (
    <div className="flex gap-8 mt-4 pb-20">
      {/* Left navigation */}
      <nav className="hidden md:block w-48 shrink-0">
        <div className="sticky top-4 space-y-1">
          <h1 className="text-2xl font-semibold px-3 mb-2">
            {translate("crm.settings.title")}
          </h1>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                document
                  .getElementById(section.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors"
            >
              {section.id === "lead-integrations"
                ? section.fallback
                : section.id === "agent-commission" ||
                    section.id === "late-interest" ||
                    section.id === "hold-exclusivity" ||
                    section.id === "portal-spend" ||
                    section.id === "whatsapp-templates" ||
                    section.id === "whatsapp-chat-presets"
                  ? translate(section.label, {
                      _: section.fallback,
                    })
                  : translate(section.label, { smart_count: 2 })}
            </button>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-2xl space-y-6">
        {/* Branding */}
        <Card id="branding">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.branding")}
            </h2>
            <TextInput source="title" label="crm.settings.app_title" />
            <div className="flex gap-8">
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.light_mode_logo")}
                </p>
                <ImageEditorField
                  source="lightModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#f5f5f5"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.dark_mode_logo")}
                </p>
                <ImageEditorField
                  source="darkModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#1a1a1a"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <LeadIntegrationsCard />

        {/* Companies */}
        <Card id="companies">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.companies.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.companies.sectors")}
            </h3>
            <ArrayInput
              source="companySectors"
              label={false}
              helperText={false}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Deals */}
        <Card id="deals">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.deals.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.currency")}
            </h3>
            <AutocompleteInput
              source="currency"
              label={false}
              choices={currencyChoices}
              inputText={(choice) => choice?.id}
              modal
            />

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.stages")}
            </h3>
            <ArrayInput
              source="dealStages"
              label={false}
              helperText={false}
              validate={validateDealStages}
            >
              <SimpleFormIterator disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.pipeline_statuses")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.deals.pipeline_help")}
            </p>
            <div className="flex flex-wrap gap-2">
              {dealStages?.map(
                (stage: { value: string; label: string }, idx: number) => {
                  const isSelected = dealPipelineStatuses.includes(stage.value);
                  return (
                    <Button
                      key={idx}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (isSelected) {
                          setValue(
                            "dealPipelineStatuses",
                            dealPipelineStatuses.filter(
                              (s) => s !== stage.value,
                            ),
                          );
                        } else {
                          setValue("dealPipelineStatuses", [
                            ...dealPipelineStatuses,
                            stage.value,
                          ]);
                        }
                      }}
                    >
                      {stage.label || stage.value}
                    </Button>
                  );
                },
              )}
            </div>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.categories")}
            </h3>
            <ArrayInput
              source="dealCategories"
              label={false}
              helperText={false}
              validate={validateDealCategories}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card id="notes">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.notes.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.notes.statuses")}
            </h3>
            <ArrayInput source="noteStatuses" label={false} helperText={false}>
              <SimpleFormIterator inline disableReordering disableClear>
                <TextInput source="label" label={false} className="flex-1" />
                <ColorInput source="color" />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card id="tasks">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.tasks.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.tasks.types")}
            </h3>
            <ArrayInput source="taskTypes" label={false} helperText={false}>
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {canEditCommission && <AgentCommissionCard />}
        {canEditCommission && <LateInterestCard />}
        {canEditCommission && <HoldExclusivityCard />}
        {canEditCommission && <PortalSpendCard />}
        {canEditCommission && <WhatsAppTemplatesCard />}
        {canEditCommission && <WhatsAppChatPresetsCard />}
      </div>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background p-4">
        <div className="max-w-screen-xl mx-auto flex gap-8 px-4">
          <div className="hidden md:block w-48 shrink-0" />
          <div className="flex-1 min-w-0 max-w-2xl flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                reset({
                  ...defaultConfiguration,
                  lightModeLogo: {
                    src: defaultConfiguration.lightModeLogo,
                  },
                  darkModeLogo: { src: defaultConfiguration.darkModeLogo },
                  agentCommission: DEFAULT_AGENT_COMMISSION_RULE,
                  portalSpend: toPortalSpendFormValues(undefined),
                  whatsappCampaignTemplates:
                    DEFAULT_WHATSAPP_CAMPAIGN_TEMPLATES.map((t) => ({
                      ...t,
                    })),
                  whatsappChatPresets: DEFAULT_WHATSAPP_CHAT_PRESETS.map(
                    (t) => ({ ...t }),
                  ),
                  lateInterestAnnualPercent:
                    DEFAULT_LATE_INTEREST_ANNUAL_PERCENT,
                  restrictHoldChangesToHolder: true,
                })
              }
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {translate("crm.settings.reset_defaults")}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
              >
                {translate("ra.action.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                onClick={(e) => {
                  const commissionErr = validateAgentCommissionRule(
                    watch("agentCommission") as AgentCommissionRule,
                  );
                  const portalSpendErr = validatePortalSpend(
                    watch("portalSpend") as Record<string, unknown>,
                  );
                  const err = commissionErr || portalSpendErr;
                  if (err) {
                    e.preventDefault();
                    notify(err, { type: "warning" });
                  }
                }}
              >
                <Save className="h-4 w-4 mr-1" />
                {isSubmitting
                  ? translate("crm.settings.saving")
                  : translate("ra.action.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatsAppTemplatesCard = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const templates = (watch("whatsappCampaignTemplates") || []) as Array<{
    id?: string;
    label?: string;
    body?: string;
    media_url?: string;
  }>;

  const updateTemplate = (
    index: number,
    patch: Partial<{ label: string; body: string; media_url: string }>,
  ) => {
    const next = templates.map((t, i) =>
      i === index ? { ...t, ...patch } : t,
    );
    setValue("whatsappCampaignTemplates", next, { shouldDirty: true });
  };

  const removeTemplate = (index: number) => {
    setValue(
      "whatsappCampaignTemplates",
      templates.filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  const addTemplate = () => {
    setValue(
      "whatsappCampaignTemplates",
      [
        ...templates,
        {
          id: `t-${Date.now().toString(36)}`,
          label: "",
          body: "",
          media_url: "",
        },
      ],
      { shouldDirty: true },
    );
  };

  return (
    <Card id="whatsapp-templates">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.whatsapp_templates", {
            _: "WhatsApp campaign templates",
          })}
        </h2>
        <p className="text-sm text-muted-foreground">
          Create, edit, or remove message templates used on WhatsApp Campaigns.
          Managers only. Paste links in the message body. Optional media URL
          must be a public HTTPS image or PDF (Meta Cloud API). Not Meta HSM
          template names. Rows need both a name and body to save; clearing all
          templates leaves the list empty (defaults are not restored).
        </p>

        <div className="space-y-4">
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-lg border border-dashed p-4">
              No templates yet. Add one to use on WhatsApp Campaigns.
            </p>
          ) : (
            templates.map((template, index) => (
              <div
                key={template.id || `template-${index}`}
                className="rounded-xl border bg-muted/20 p-4 space-y-3 shadow-xs"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Template {index + 1}
                    </p>
                    <p className="text-sm font-medium text-foreground truncate">
                      {template.label?.trim() || "Untitled template"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => removeTemplate(index)}
                    aria-label={`Remove template ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor={`wa-template-label-${index}`}
                    className="text-xs font-semibold text-foreground"
                  >
                    Template name
                  </label>
                  <Input
                    id={`wa-template-label-${index}`}
                    value={template.label ?? ""}
                    onChange={(e) =>
                      updateTemplate(index, { label: e.target.value })
                    }
                    placeholder="e.g. Weekend site visit invite"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor={`wa-template-body-${index}`}
                    className="text-xs font-semibold text-foreground"
                  >
                    Message body
                  </label>
                  <textarea
                    id={`wa-template-body-${index}`}
                    value={template.body ?? ""}
                    onChange={(e) =>
                      updateTemplate(index, { body: e.target.value })
                    }
                    rows={3}
                    placeholder="Message text (sent after Namaste {name}!). Links can go here."
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor={`wa-template-media-${index}`}
                    className="text-xs font-semibold text-foreground"
                  >
                    Image or PDF URL{" "}
                    <span className="font-normal text-muted-foreground">
                      (optional)
                    </span>
                  </label>
                  <Input
                    id={`wa-template-media-${index}`}
                    value={template.media_url ?? ""}
                    onChange={(e) =>
                      updateTemplate(index, { media_url: e.target.value })
                    }
                    placeholder="https://…/brochure.pdf"
                  />
                </div>
              </div>
            ))
          )}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addTemplate}>
          <Plus className="h-4 w-4 mr-1.5" />
          Add template
        </Button>
      </CardContent>
    </Card>
  );
};

const WhatsAppChatPresetsCard = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const presets = (watch("whatsappChatPresets") || []) as Array<{
    id?: string;
    title?: string;
    body?: string;
  }>;

  const updatePreset = (
    index: number,
    patch: Partial<{ title: string; body: string }>,
  ) => {
    const next = presets.map((p, i) => (i === index ? { ...p, ...patch } : p));
    setValue("whatsappChatPresets", next, { shouldDirty: true });
  };

  const removePreset = (index: number) => {
    setValue(
      "whatsappChatPresets",
      presets.filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  const addPreset = () => {
    if (presets.length >= MAX_WHATSAPP_CHAT_PRESETS) return;
    setValue(
      "whatsappChatPresets",
      [
        ...presets,
        {
          id: `p-${Date.now().toString(36)}`,
          title: "",
          body: "",
        },
      ],
      { shouldDirty: true },
    );
  };

  return (
    <Card id="whatsapp-chat-presets">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.whatsapp_chat_presets", {
            _: "WhatsApp chat presets",
          })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.whatsapp_chat_presets.help", {
            _: "Quick-reply buttons in the contact WhatsApp chat. Managers only. Use {name} for the contact name. Up to %{max} presets.",
            max: MAX_WHATSAPP_CHAT_PRESETS,
          })}
        </p>

        <div className="space-y-4">
          {presets.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-lg border border-dashed p-4">
              {translate("crm.settings.whatsapp_chat_presets.empty", {
                _: "No chat presets yet. Add one for agents to use in WhatsApp chat.",
              })}
            </p>
          ) : (
            presets.map((preset, index) => (
              <div
                key={preset.id || `preset-${index}`}
                className="rounded-xl border bg-muted/20 p-4 space-y-3 shadow-xs"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Preset {index + 1}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => removePreset(index)}
                    aria-label={`Remove preset ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor={`wa-chat-preset-title-${index}`}
                    className="text-xs font-semibold text-foreground"
                  >
                    {translate(
                      "crm.settings.whatsapp_chat_presets.title_label",
                      {
                        _: "Button label",
                      },
                    )}
                  </label>
                  <Input
                    id={`wa-chat-preset-title-${index}`}
                    value={preset.title ?? ""}
                    onChange={(e) =>
                      updatePreset(index, { title: e.target.value })
                    }
                    placeholder={translate(
                      "crm.settings.whatsapp_chat_presets.title_placeholder",
                      { _: "e.g. Site Visit Invite" },
                    )}
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor={`wa-chat-preset-body-${index}`}
                    className="text-xs font-semibold text-foreground"
                  >
                    {translate(
                      "crm.settings.whatsapp_chat_presets.body_label",
                      {
                        _: "Message body",
                      },
                    )}
                  </label>
                  <textarea
                    id={`wa-chat-preset-body-${index}`}
                    value={preset.body ?? ""}
                    onChange={(e) =>
                      updatePreset(index, { body: e.target.value })
                    }
                    rows={3}
                    placeholder={translate(
                      "crm.settings.whatsapp_chat_presets.body_placeholder",
                      {
                        _: "Namaste {name}! We would love to host you for a site visit…",
                      },
                    )}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {presets.length < MAX_WHATSAPP_CHAT_PRESETS && (
          <Button type="button" variant="outline" size="sm" onClick={addPreset}>
            <Plus className="h-4 w-4 mr-1.5" />
            {translate("crm.settings.whatsapp_chat_presets.add", {
              _: "Add preset",
            })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const PortalSpendCard = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const portalSpend = (watch("portalSpend") || {}) as Record<
    string,
    number | ""
  >;

  const updateSpend = (portalId: string, raw: string) => {
    setValue("portalSpend", {
      ...portalSpend,
      [portalId]: raw === "" ? "" : Number(raw),
    });
  };

  return (
    <Card id="portal-spend">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.portal_spend")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.portal_spend.help")}
        </p>
        <div className="space-y-3">
          {PORTAL_SPEND_SOURCES.map((portal) => (
            <div
              key={portal.id}
              className="grid grid-cols-[1fr_140px] gap-3 items-center"
            >
              <label
                htmlFor={`portal-spend-${portal.id}`}
                className="text-sm font-medium text-foreground"
              >
                {portal.label}
              </label>
              <Input
                id={`portal-spend-${portal.id}`}
                type="number"
                min={0}
                step={1000}
                placeholder="₹"
                value={portalSpend[portal.id] ?? ""}
                onChange={(e) => updateSpend(portal.id, e.target.value)}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const LateInterestCard = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const rate = normalizeLateInterestAnnualPercent(
    watch("lateInterestAnnualPercent"),
  );

  return (
    <Card id="late-interest">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.late_interest", {
            _: "Late interest",
          })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.late_interest.help", {
            _: "Annual simple interest applied to overdue milestone balances on demand letters (principal × rate × days / 365).",
          })}
        </p>
        <div className="space-y-1 max-w-xs">
          <label className="text-sm font-medium text-muted-foreground">
            {translate("crm.settings.late_interest.rate_label", {
              _: "Annual late interest rate (%)",
            })}
          </label>
          <Input
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={rate}
            onChange={(e) =>
              setValue(
                "lateInterestAnnualPercent",
                normalizeLateInterestAnnualPercent(e.target.value),
              )
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};

const HoldExclusivityCard = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const enabled = watch("restrictHoldChangesToHolder") !== false;

  return (
    <Card id="hold-exclusivity">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.hold_exclusivity", {
            _: "Hold exclusivity",
          })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.hold_exclusivity.help", {
            _: "When on (recommended), only the agent who held the unit and managers/admins can release or change that hold. When off, any agent can change holds.",
          })}
        </p>
        <div className="flex items-center justify-between gap-4 max-w-lg">
          <label
            htmlFor="restrict-hold-changes"
            className="text-sm font-medium text-foreground"
          >
            {translate("crm.settings.hold_exclusivity.toggle_label", {
              _: "Restrict hold changes to holding agent + managers",
            })}
          </label>
          <Switch
            id="restrict-hold-changes"
            checked={enabled}
            onCheckedChange={(checked) =>
              setValue("restrictHoldChangesToHolder", checked)
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};

const AgentCommissionCard = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const agentCommission = watch("agentCommission") as
    | AgentCommissionRule
    | undefined;

  const preview = useMemo(
    () =>
      computeCommissions({
        agreementValue: SAMPLE_PREVIEW_AGREEMENT,
        agentRule: agentCommission,
      }),
    [agentCommission],
  );

  const setMode = (mode: AgentCommissionRule["type"]) => {
    if (mode === "percent") {
      const rate =
        agentCommission?.type === "slab"
          ? (agentCommission.slabs[0]?.rate ??
            DEFAULT_AGENT_COMMISSION_RULE.rate)
          : agentCommission?.type === "percent"
            ? agentCommission.rate
            : DEFAULT_AGENT_COMMISSION_RULE.rate;
      setValue("agentCommission", { type: "percent", rate: Number(rate) || 0 });
      return;
    }
    const slabs =
      agentCommission?.type === "slab" && agentCommission.slabs.length
        ? agentCommission.slabs
        : [
            {
              minAgreementValue: 0,
              rate:
                agentCommission?.type === "percent"
                  ? agentCommission.rate
                  : DEFAULT_AGENT_COMMISSION_RULE.rate,
            },
          ];
    setValue("agentCommission", { type: "slab", slabs });
  };

  const updateSlab = (
    index: number,
    field: "minAgreementValue" | "rate",
    raw: string,
  ) => {
    if (agentCommission?.type !== "slab") return;
    const slabs = agentCommission.slabs.map((slab, i) =>
      i === index
        ? {
            ...slab,
            [field]:
              field === "minAgreementValue"
                ? Number(raw) || 0
                : Number(raw) || 0,
          }
        : slab,
    );
    setValue("agentCommission", { type: "slab", slabs });
  };

  const addSlab = () => {
    if (agentCommission?.type !== "slab") return;
    const last = agentCommission.slabs[agentCommission.slabs.length - 1];
    setValue("agentCommission", {
      type: "slab",
      slabs: [
        ...agentCommission.slabs,
        {
          minAgreementValue: (last?.minAgreementValue ?? 0) + 10_000_000,
          rate: last?.rate ?? DEFAULT_AGENT_COMMISSION_RULE.rate,
        },
      ],
    });
  };

  const removeSlab = (index: number) => {
    if (agentCommission?.type !== "slab") return;
    if (agentCommission.slabs.length <= 1) return;
    setValue("agentCommission", {
      type: "slab",
      slabs: agentCommission.slabs.filter((_, i) => i !== index),
    });
  };

  return (
    <Card id="agent-commission">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.agent_commission")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.agent_commission.help")}
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={agentCommission?.type !== "slab" ? "default" : "outline"}
            onClick={() => setMode("percent")}
          >
            {translate("crm.settings.agent_commission.mode_percent")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={agentCommission?.type === "slab" ? "default" : "outline"}
            onClick={() => setMode("slab")}
          >
            {translate("crm.settings.agent_commission.mode_slab")}
          </Button>
        </div>

        {agentCommission?.type === "slab" ? (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-semibold text-muted-foreground px-1">
              <span>
                {translate("crm.settings.agent_commission.slab_min_agreement")}
              </span>
              <span>
                {translate("crm.settings.agent_commission.slab_rate")}
              </span>
              <span className="w-8" />
            </div>
            {agentCommission.slabs.map((slab, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
              >
                <Input
                  type="number"
                  min={0}
                  step={100000}
                  value={slab.minAgreementValue}
                  onChange={(e) =>
                    updateSlab(index, "minAgreementValue", e.target.value)
                  }
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={slab.rate}
                  onChange={(e) => updateSlab(index, "rate", e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  disabled={agentCommission.slabs.length <= 1}
                  onClick={() => removeSlab(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addSlab}>
              <Plus className="h-4 w-4 mr-1" />
              {translate("crm.settings.agent_commission.add_slab")}
            </Button>
          </div>
        ) : (
          <div className="space-y-1 max-w-xs">
            <label className="text-sm font-medium text-muted-foreground">
              {translate("crm.settings.agent_commission.rate_label")}
            </label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={
                agentCommission?.type === "percent" ? agentCommission.rate : 0
              }
              onChange={(e) =>
                setValue("agentCommission", {
                  type: "percent",
                  rate: Number(e.target.value) || 0,
                })
              }
            />
          </div>
        )}

        <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
          <p className="font-medium text-foreground">
            {translate("crm.settings.agent_commission.preview_title")}
          </p>
          <p className="text-muted-foreground">
            {translate("crm.settings.agent_commission.preview_sample", {
              amount: formatPriceInINR(SAMPLE_PREVIEW_AGREEMENT),
            })}
          </p>
          <p className="text-foreground">
            {translate("crm.settings.agent_commission.preview_result", {
              rate: preview.agentRate,
              payout: formatPriceInINR(preview.agentAmount),
            })}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};

const LeadIntegrationsCard = () => {
  const notify = useNotify();
  const [copied, setCopied] = useState<string | null>(null);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
  const emailParserUrl = supabaseUrl
    ? `${supabaseUrl.replace(/\/$/, "")}/functions/v1/portal-email-parser`
    : "";

  const { data: contacts } = useGetList("contacts", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "last_seen", order: "DESC" },
    filter: {},
  });

  const since = useMemo(
    () => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    [],
  );
  const { data: inboundRows } = useGetList("inbound_leads", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "created_at", order: "DESC" },
    filter: { "created_at@gte": since },
  });

  const bySource = useMemo(
    () => summarizeLeadSourcesByPortal(contacts || []),
    [contacts],
  );

  const last24h = useMemo(
    () => summarizeInboundLeadsLast24h(inboundRows || []),
    [inboundRows],
  );

  const copyUrl = async (key: string, url: string) => {
    if (!url) {
      notify("Set VITE_SUPABASE_URL to build webhook URLs.", {
        type: "warning",
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(key);
      notify("Webhook URL copied", { type: "info" });
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      notify("Could not copy — select the URL manually.", { type: "warning" });
    }
  };

  return (
    <Card id="lead-integrations">
      <CardContent className="space-y-4">
        <div className="flex items-start gap-2">
          <Webhook className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <h2 className="text-xl font-semibold text-muted-foreground">
              Lead integrations
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Point portal webhooks or Zapier at these URLs. Auth header:{" "}
              <code className="text-xs bg-muted px-1 rounded">
                x-webhook-secret
              </code>{" "}
              = Supabase secret{" "}
              <code className="text-xs bg-muted px-1 rounded">
                PORTAL_WEBHOOK_SECRET
              </code>
              . Email portals: forward to Postmark inbound →{" "}
              <code className="text-xs bg-muted px-1 rounded break-all">
                {emailParserUrl || "…/portal-email-parser"}
              </code>
              .
            </p>
          </div>
        </div>

        <div className="rounded-lg border bg-background p-3 text-xs space-y-2">
          <div className="font-semibold text-sm text-foreground">
            Last 24 hours
          </div>
          <div className="flex flex-wrap gap-3 text-muted-foreground">
            <span>
              OK: <strong className="text-foreground">{last24h.ok}</strong>
            </span>
            <span>
              Failed:{" "}
              <strong className="text-foreground">{last24h.failed}</strong>
            </span>
          </div>
          {Object.keys(last24h.byPortal).length === 0 ? (
            <p className="text-muted-foreground">
              No ingest events logged yet (table fills when webhooks fire).
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(last24h.byPortal).map(([portal, count]) => (
                <Badge key={portal} variant="outline">
                  {portal}: {count}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {LEAD_PORTAL_SOURCES.map((portal) => {
            const url = buildPortalIngestUrl(supabaseUrl, portal.portalParam);
            const stats =
              bySource[portal.id] ||
              bySource[portal.portalParam] ||
              bySource[portal.portalParam.replace("_", " ")];
            const receiving = (stats?.count || 0) > 0;
            return (
              <div
                key={portal.id}
                className="rounded-lg border bg-muted/20 p-3 space-y-2"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-sm">{portal.label}</div>
                  <Badge variant={receiving ? "default" : "outline"}>
                    {receiving ? "Receiving" : "Not seen"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {portal.delivery}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Last lead: {formatLastLeadAt(stats?.lastAt || null)}
                  {stats?.count ? ` · ${stats.count} contact(s)` : ""}
                </p>
                <div className="flex gap-2 items-start">
                  <code className="flex-1 text-[10px] break-all bg-background border rounded px-2 py-1.5">
                    {url || "(set VITE_SUPABASE_URL)"}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => copyUrl(portal.id, url)}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {copied === portal.id ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
